/**
 * React Query hooks for the live, read-only Azure data behind the Apprise
 * "Create a virtual machine" form (contract v2 §3).
 *
 * The backend caches every answer for 5 minutes and never returns a 5xx for an
 * Azure problem (HTTP 200 + source "fallback" instead). When the request itself
 * fails we fall back to the built-in defaults here, so the form stays usable.
 */
import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { productsApi } from '@/services/products'
import { formatApiError } from '@/utils/format'
import { cleanCatalogParams, fallbackItems, sameImage, type CatalogResult } from '@/components/apprise-vm/appriseCatalog'
import type { AzureCatalogItems, AzureCatalogKind, AzureCatalogParams, AzureSubscriptionInfo } from '@/types'

const CATALOG_STALE_MS = 5 * 60 * 1000
const FALLBACK_STALE_MS = 20 * 1000

export const azureKeys = {
  all: ['products', 'apprise-vm', 'azure'] as const,
  subscription: () => [...azureKeys.all, 'subscription'] as const,
  catalog: (kind: AzureCatalogKind, params: AzureCatalogParams) => [...azureKeys.all, 'catalog', kind, params] as const,
}

/** The pinned subscription (read-only in the form). */
export function useAzureSubscription(enabled: boolean) {
  return useQuery<AzureSubscriptionInfo>({
    queryKey: azureKeys.subscription(),
    queryFn: async () => (await productsApi.getAppriseAzureSubscription()).data,
    enabled,
    staleTime: CATALOG_STALE_MS,
    retry: 1,
  })
}

/**
 * One live catalog. `items` is always usable: Azure's answer, the backend's own
 * fallback (source "fallback") or — when the call failed — the built-in defaults.
 */
export function useAzureCatalog<K extends AzureCatalogKind>(
  kind: K,
  params: AzureCatalogParams = {},
  enabled = true,
): CatalogResult<AzureCatalogItems[K]> {
  const clean = cleanCatalogParams(params)
  const query = useQuery({
    queryKey: azureKeys.catalog(kind, clean),
    queryFn: async () => (await productsApi.getAppriseAzureCatalog(kind, clean)).data,
    enabled,
    // a backend fallback (Azure was unreachable) is retried soon; real Azure data is cached like the backend does
    staleTime: (q) => (q.state.data?.source === 'fallback' ? FALLBACK_STALE_MS : CATALOG_STALE_MS),
    retry: 1,
    // keep the previous list on screen while a new search is in flight
    placeholderData: keepPreviousData,
  })

  const { data, error, isLoading, isFetching } = query
  const q = clean.q ?? ''
  return useMemo<CatalogResult<AzureCatalogItems[K]>>(() => {
    if (data) {
      return { items: data.items ?? [], source: data.source ?? 'azure', error: data.error ?? '', isLoading: false, isFetching }
    }
    if (error) {
      return { items: fallbackItems(kind, q), source: 'fallback', error: formatApiError(error), isLoading: false, isFetching }
    }
    return { items: [], source: 'azure', error: '', isLoading: enabled && isLoading, isFetching }
  }, [data, error, isLoading, isFetching, kind, q, enabled])
}

/**
 * The live details (vCPU, memory, price) of the chosen size, looked up by its exact name so the
 * summary does not depend on what the size search box currently shows.
 */
/**
 * The live details (Hyper-V generation, architecture) of the image the requester has picked, looked up by its exact
 * publisher / offer / SKU so the compatibility check does not depend on what the image search box currently shows.
 */
export function useSelectedImage(location: string, image: { publisher: string; offer: string; sku: string; version: string }, enabled = true) {
  const catalog = useAzureCatalog('images', { location, q: image.sku }, enabled && !!image.sku.trim())
  const found = catalog.items.find((i) => sameImage(i, image))
  return { image: found, catalog }
}

export function useSelectedSize(location: string, vmSize: string, enabled = true) {
  const catalog = useAzureCatalog('sizes', { location, q: vmSize }, enabled && !!vmSize.trim())
  const size = catalog.items.find((s) => s.name.toLowerCase() === vmSize.trim().toLowerCase())
  return { size, catalog }
}
